import React from 'react';
import { 
  Container, Paper, Box, Typography, Button, IconButton, 
  Snackbar, Alert, CircularProgress, Divider, Tooltip, TextField, Grid
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import { ThemeProvider } from '@mui/material/styles';

import theme from '../../../theme/theme';
import { DRAFT_KEYS } from '../../../utils/draftUtils';
import DraftSaveButton from '../../../components/DraftSaveButton';

import { usePostOpHandover } from './hooks/usePostOpHandover';
import ReportsSection from './screens/ReportsSection';
import HandoverDetailsSection from './screens/HandoverDetailsSection';
import SignaturesSection from './screens/SignaturesSection';

const PostOperativeHandover = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData, setFormData,
    errors, 
    success,
    loading,
    submitStatus, setSubmitStatus,
    signatureDialogOpen,
    signaturePadRef,
    handleInputChange, handleReportChange, handleBlur,
    handleSignatureOpen, handleSignatureClear, handleSignatureSave, handleSignatureClose,
    handleSubmit, handleShare,
    mutationLoading
  } = usePostOpHandover(uhid_no, navigate);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PostOperativePhase', status: 'success', step: 3 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = (formName, uhid) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid)}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: '16px' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ color: '#008080', fontWeight: 700, letterSpacing: 1 }}>
                POST-OPERATIVE HANDOVER
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: 40, height: 40 }} />
              <Typography variant="h5" sx={{ color: '#008080', fontWeight: 700 }}>OTMS</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Button variant="outlined" onClick={handleBack} sx={{ borderRadius: '8px' }}>Back</Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button 
                variant="contained" 
                onClick={handleHomeNavigation}
                sx={{ borderRadius: '8px', bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
              >
                Home
              </Button>
              <Button 
                variant="contained" 
                onClick={() => goToVideoRecorder("Post Operative Handover", uhid_no)}
                sx={{ borderRadius: '8px', bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
              >
                Add Video
              </Button>
              <Tooltip title="Share with Doctor">
                <IconButton onClick={handleShare} sx={{ color: '#008080', border: '1px solid #008080' }}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="UHID No."
                  value={formData.uhid_no}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IP No."
                  value={formData.ip_no}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 6 }}>
              <ReportsSection 
                formData={formData}
                handleReportChange={handleReportChange}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <HandoverDetailsSection 
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleInputChange={handleInputChange}
                handleBlur={handleBlur}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <SignaturesSection 
                formData={formData}
                signatureDialogOpen={signatureDialogOpen}
                signaturePadRef={signaturePadRef}
                handleSignatureOpen={handleSignatureOpen}
                handleSignatureClear={handleSignatureClear}
                handleSignatureSave={handleSignatureSave}
                handleSignatureClose={handleSignatureClose}
              />
            </Box>

            {/* Form Actions */}
            <Box sx={{ display: 'flex', gap: 2, mt: 6 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.POST_OPERATIVE_ORDERS} // Reusing or changing as needed
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                variant="outlined"
                size="large"
                isUpdate={success}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={mutationLoading}
                startIcon={mutationLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ 
                  borderRadius: '10px', 
                  bgcolor: '#008080', 
                  py: 1.5,
                  '&:hover': { bgcolor: '#006666' } 
                }}
              >
                {mutationLoading ? 'Submitting...' : (success ? 'Update Handover' : 'Submit Handover')}
              </Button>
            </Box>
          </form>

          {/* Feedback SnackBar */}
          <Snackbar 
            open={submitStatus.open} 
            autoHideDuration={6000} 
            onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))} 
              severity={submitStatus.severity} 
              sx={{ width: '100%', borderRadius: '8px' }}
            >
              {submitStatus.message}
            </Alert>
          </Snackbar>

          {success && (
            <Alert severity="info" sx={{ mt: 3, borderRadius: '8px' }}>
              You are updating an existing handover record for this patient.
            </Alert>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PostOperativeHandover;
